import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useNavigate } from 'react-router-dom';
import Header from '../components/layout/Header';
import Input from '../components/common/Input';
import Button from '../components/common/Button';
import LanguageSelector from '../components/common/LanguageSelector';
import { User, Trash2, ArrowLeft } from 'lucide-react';
import { motion } from 'framer-motion';

const ProfilePage: React.FC = () => {
  const { user, updateProfile, deleteAccount, isLoading, error } = useAuth();
  const { labels, language } = useLanguage();
  const navigate = useNavigate();
  const [name, setName] = useState(user?.name || '');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await updateProfile(name.trim(), language);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAccount = async () => {
    await deleteAccount();
    setShowDeleteConfirm(false);
  };

  return (
    <div className="min-h-screen bg-primary-50">
      <Header />
      
      <main className="max-w-2xl mx-auto px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="mb-6">
            <Button
              variant="ghost"
              size="sm"
              leftIcon={<ArrowLeft size={16} />}
              onClick={() => navigate('/chat')}
              className="mb-4"
            >
              {labels.backToChat}
            </Button>
                    <h1 className="text-3xl font-bold text-primary-900">{labels.profileSettings}</h1>
        <p className="text-primary-700 mt-2 font-medium">{labels.profileSubtitle}</p>
          </div>

                      <div className="bg-white rounded-lg shadow-sm border-2 border-primary-200 p-6 mb-6">
            <h2 className="text-xl font-semibold text-primary-900 mb-4">{labels.personalInformation}</h2>
            
            <form onSubmit={handleUpdateProfile} className="space-y-4">
              <div>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  required
                  label={labels.displayName}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  leftIcon={<User size={18} />}
                  fullWidth
                />
              </div>
              
              <div>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  label={labels.emailAddress}
                  value={user?.email || ''}
                  disabled
                  fullWidth
                  className="bg-primary-50"
                />
                <p className="text-sm text-primary-500 mt-1">{labels.emailCannotBeChanged}</p>
              </div>

              <div>
                <label className="mb-1.5 block text-sm font-semibold text-primary-700">
                  {labels.language}
                </label>
                <LanguageSelector />
              </div>

              {error && (
                <div className="bg-error-50 text-error-700 p-3 rounded-md text-sm">
                  {error}
                </div>
              )}

              <div className="flex justify-end">
                <Button
                  type="submit"
                  variant="primary"
                  isLoading={isLoading || saving}
                  disabled={
                    (!name.trim() || name === user?.name) &&
                    (language === user?.user_metadata?.language)
                  }
                >
                  {labels.updateProfile}
                </Button>
              </div>
            </form>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-error-200 p-6">
            <h2 className="text-xl font-semibold text-error-700 mb-4">{labels.dangerZone}</h2>
            <p className="text-dark-600 mb-4">
              {labels.deleteAccountWarning}
            </p>
            
            <Button
              variant="secondary"
              leftIcon={<Trash2 size={16} />}
              onClick={() => setShowDeleteConfirm(true)}
              className="border-error-300 text-error-700 hover:bg-error-50"
            >
                                    {labels.deleteAccount}
            </Button>
          </div>

          {showDeleteConfirm && (
            <motion.div
              className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <motion.div
                className="bg-white rounded-lg p-6 max-w-md mx-4"
                initial={{ scale: 0.9 }}
                animate={{ scale: 1 }}
              >
                <h3 className="text-lg font-semibold text-primary-900 mb-2">{labels.deleteAccount}</h3>
                        <p className="text-primary-700 mb-4 font-medium">
          {labels.deleteAccountConfirmText}
                </p>
                <div className="flex space-x-3">
                  <Button
                    variant="secondary"
                    onClick={() => setShowDeleteConfirm(false)}
                    className="flex-1"
                  >
                    {labels.cancel}
                  </Button>
                  <Button
                    variant="primary"
                    onClick={handleDeleteAccount}
                    isLoading={isLoading}
                    className="flex-1 bg-error-600 hover:bg-error-700"
                  >
                    {labels.deleteAccount}
                  </Button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </motion.div>
      </main>
    </div>
  );
};

export default ProfilePage; 